/* global axios */

import ApiClient from '../ApiClient';

/**
 * Конфигурация для внешнего OpenAI API
 */
const EXTERNAL_OPENAI_CONFIG = {
  // URL внешнего контроллера (внутренний сервис)
  baseUrl: import.meta.env.VITE_EXTERNAL_OPENAI_API_URL || '/api/v1/assistants',
  // Таймаут запросов
  timeout: 60000,
};

/**
 * Клиент для работы с внешним OpenAI API
 * Работает с внутренним сервисом, который уже имеет API ключ
 */
class ExternalOpenAIAPI {
  constructor() {
    this.baseUrl = EXTERNAL_OPENAI_CONFIG.baseUrl;

    // Поддерживаемые события (только два)
    this.supportedEvents = [
      'reply_suggestion',
      'summarize',
    ];
  }

  /**
   * Получает предложение ответа для разговора
   * @param {string} conversationId - ID разговора
   * @returns {Promise<Object>} Предложение ответа
   */
  async getReplySuggestion(conversationId) {
    try {
      const response = await axios.get(`${this.baseUrl}/playground`, {
        params: {
          type: 'reply_suggestion',
          conversation_id: conversationId,
        },
        timeout: EXTERNAL_OPENAI_CONFIG.timeout,
      });
      
      return {
        data: {
          message: response.data.message || response.data.result,
          success: true,
        },
      };
    } catch (error) {
      console.error('Ошибка при получении предложения ответа:', error);
      
      throw {
        response: {
          data: {
            error: {
              message: error.response?.data?.message || 'Ошибка получения предложения ответа',
            },
          },
        },
      };
    }
  }

  /**
   * Суммирует разговор
   * @param {string} conversationId - ID разговора
   * @returns {Promise<Object>} Резюме разговора
   */
  async summarizeConversation(conversationId) {
    try {
      const response = await axios.get(`${this.baseUrl}/playground`, {
        params: {
          type: 'summarize',
          conversation_id: conversationId,
        },
        timeout: EXTERNAL_OPENAI_CONFIG.timeout,
      });
      
      return {
        data: {
          message: response.data.message || response.data.result,
          success: true,
        },
      };
    } catch (error) {
      console.error('Ошибка при получении резюме разговора:', error);
      
      throw {
        response: {
          data: {
            error: {
              message: error.response?.data?.message || 'Ошибка получения резюме разговора',
            },
          },
        },
      };
    }
  }

  /**
   * Отправляет обратную связь о некорректном ответе AI
   * @param {string} conversationId - ID разговора
   * @param {string} incorrectAnswer - Некорректный ответ AI
   * @param {string} correctAnswer - Правильный ответ от агента
   * @returns {Promise<Object>} Результат отправки feedback
   */
  async sendFeedback(conversationId, incorrectAnswer, correctAnswer) {
    try {
      const response = await axios.post(`${this.baseUrl}/feedback`, {
        incorrect_answer: incorrectAnswer,
        correct_answer: correctAnswer,
      }, {
        params: {
          conversation_id: conversationId,
        },
        timeout: EXTERNAL_OPENAI_CONFIG.timeout,
      });
      
      return {
        data: {
          success: true,
          message: response.data.message || 'Feedback отправлен успешно',
        },
      };
    } catch (error) {
      console.error('Ошибка при отправке feedback:', error);
      
      throw {
        response: {
          data: {
            error: {
              message: error.response?.data?.message || 'Ошибка отправки feedback',
            },
          },
        },
      };
    }
  }

  /**
   * Универсальный метод для обработки событий (для совместимости)
   * @param {Object} options - Параметры события
   * @param {string} options.type - Тип события
   * @param {string} [options.conversationId] - ID разговора
   * @returns {Promise<Object>} Результат обработки события
   */
  async processEvent({ type, conversationId }) {
    if (!this.supportedEvents.includes(type)) {
      throw new Error(`Неподдерживаемый тип события: ${type}`);
    }

    switch (type) {
      case 'reply_suggestion':
        return this.getReplySuggestion(conversationId);
      case 'summarize':
        return this.summarizeConversation(conversationId);
      default:
        throw new Error(`Неподдерживаемый тип события: ${type}`);
    }
  }
}

export default new ExternalOpenAIAPI(); 
